// Single-line text input. Drives `z.string()` (no `.url()` /
// `.describe('richText')` / `.describe('imageUrl')` brand markers) per
// spec/tech-arch.md §7.
//
// **Controlled API.** Consumer holds `value` and passes `onChange` (called
// with the next string or `undefined`). No internal state.
//
// **Native HTML constraints + Zod validation.** `minLength` / `maxLength`
// are surfaced as native attributes for browser-side hint UX, but the
// authoritative validation runs at the form wrapper via `safeParse`. The
// `error` prop carries the message to display inline.
//
// **P-037.31 fourth post-Phase-Close §B — empty input → onChange(undefined).**
// When the input value is `''` (user typed text then deleted, or field
// initialized empty), dispatch `undefined` instead of `''`. The form's
// `writeField` merge produces `{ ...obj, [name]: undefined }` which Zod's
// `.optional()` accepts identically to "key absent" — `z.string().min(1).
// optional()` (e.g., `events-list` per-event `audience`) then validates
// cleanly. Without the coercion, `''` is a present string that fails
// `.min(1)` and surfaces "String must contain at least 1 character(s)" on
// an OPTIONAL field. For REQUIRED fields (`z.string().min(1)` WITHOUT
// `.optional()`) Zod surfaces "Required" on undefined — semantically
// correct. Same boundary-coercion pattern as `renderSelect` (optional-enum
// `''` → undefined) + `<ImageUrlField>` Clear (P-037.31 §A).

import { FieldShell } from './field-shell'

interface TextFieldProps {
  readonly id: string
  readonly name: string
  readonly label: string
  readonly helpText?: string
  readonly value: string
  // P-037.31 §B — `undefined` is the empty sentinel for optional fields
  // (was `''`). The form wrapper's writeField merges undefined as
  // "key present with undefined value", which Zod's `.optional()`
  // accepts identically to "key absent".
  readonly onChange: (next: string | undefined) => void
  readonly onBlur?: () => void
  readonly error?: string
  readonly required?: boolean
  readonly minLength?: number
  readonly maxLength?: number
}

export function TextField({
  id,
  name,
  label,
  helpText,
  value,
  onChange,
  onBlur,
  error,
  required = false,
  minLength,
  maxLength,
}: TextFieldProps) {
  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      {...(helpText !== undefined ? { helpText } : {})}
      {...(error !== undefined ? { error } : {})}
    >
      {(slot) => (
        <input
          {...slot.attrs}
          name={name}
          type="text"
          value={value}
          onInput={(e) => {
            // P-037.31 §B — coerce empty → undefined at the dispatch
            // boundary so optional-string `.min(1)` schemas validate
            // cleanly on field clear. See file header for full rationale.
            const next = (e.currentTarget as HTMLInputElement).value
            onChange(next === '' ? undefined : next)
          }}
          onBlur={onBlur}
          minLength={minLength}
          maxLength={maxLength}
          class={slot.inputClass}
        />
      )}
    </FieldShell>
  )
}
